import type { ReactNode } from 'react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart'
import { niceTicks } from './chartCardFormat'

/**
 * Shared plot anatomy for `TrendLineChart` and `PeriodBarChart` (Paper
 * "Charts · Card pattern · Oct 2026", Anatomy 5 to 11): 200px tall with a
 * 164px plot, Y labels left in a 44px gutter, X labels 20px under the plot,
 * dotted 1px gridlines (dash "1 3", round caps), 4 lines including zero, no
 * baseline and no vertical lines.
 */

export interface PeriodPoint {
  /** X axis label: "Sep 19" */
  label: string
  /** Tooltip header: "Wed, Sep 23" */
  tooltipLabel: string
  current: number
  /** Same bucket position in the previous window; null when unknown */
  previous: number | null
}

export const CHART_HEIGHT = 200
export const PLOT_TOP = 8
/** 200 − 8 top − 164 plot */
export const X_AXIS_HEIGHT = 28
export const Y_GUTTER = 44

/** Previous-period colors (mist-400 line, mist-200 ghost bar, mist-300 swatch and crosshair) */
export const PREV_LINE = 'var(--color-neutral-400)'
export const PREV_BAR = 'var(--color-neutral-200)'
export const PREV_SWATCH = 'var(--color-neutral-300)'
export const CROSSHAIR = 'var(--color-neutral-300)'

const AXIS_TEXT = { fontSize: 11, fill: 'var(--foreground)', fillOpacity: 0.45 }

export const gridProps = {
  vertical: false,
  stroke: 'var(--chart-grid)',
  strokeDasharray: '1 3',
  strokeLinecap: 'round' as const,
}

/** Y domain and ticks, always finite (a NaN bound blanks the whole chart). */
export function yScale(points: PeriodPoint[], withPrevious: boolean): { ticks: number[]; domain: [number, number] } {
  const max = points.reduce(
    (m, p) => Math.max(m, Number.isFinite(p.current) ? p.current : 0, withPrevious && p.previous != null && Number.isFinite(p.previous) ? p.previous : 0),
    0,
  )
  const ticks = niceTicks(max)
  return { ticks, domain: [0, ticks[ticks.length - 1] || 1] }
}

/** Y tick: left-aligned in the gutter, 11px at 45% ink */
function YTick({ y, payload, format }: { y?: number; payload?: { value: number }; format: (n: number) => string }) {
  if (y == null || !payload) return null
  return (
    <text x={0} y={y + 4} {...AXIS_TEXT} textAnchor="start" fontFamily="inherit">
      {format(payload.value)}
    </text>
  )
}

/** X tick: centred, baseline 20px under the plot */
function XTick({
  x,
  y,
  payload,
  index = 0,
  thin = false,
}: {
  x?: number
  y?: number
  payload?: { value: string }
  index?: number
  /** Hide every other label on a phone so seven dates never collide */
  thin?: boolean
}) {
  if (x == null || y == null || !payload) return null
  return (
    <text
      x={x}
      y={y + 20}
      {...AXIS_TEXT}
      textAnchor="middle"
      fontFamily="inherit"
      className={thin && index % 2 === 1 ? 'max-sm:hidden' : undefined}
    >
      {payload.value}
    </text>
  )
}

/**
 * Up to a week of buckets: every label (Paper labels all seven days), with
 * every other one hidden on a phone. Longer series thin out by gap.
 */
export function xAxis(pointCount = 0, minTickGap = 24) {
  return (
    <XAxis
      dataKey="label"
      axisLine={false}
      tickLine={false}
      tickSize={0}
      height={X_AXIS_HEIGHT}
      interval={pointCount > 0 && pointCount <= 8 ? 0 : 'preserveStartEnd'}
      minTickGap={minTickGap}
      tick={<XTick thin={pointCount > 4} />}
    />
  )
}

export function yAxis(ticks: number[], domain: [number, number], format: (n: number) => string) {
  return (
    <YAxis
      width={Y_GUTTER}
      axisLine={false}
      tickLine={false}
      ticks={ticks}
      domain={domain}
      interval={0}
      allowDataOverflow={false}
      tick={<YTick format={format} />}
    />
  )
}

/** One tooltip row: 10px swatch, label at 60% white, value white, right-aligned */
export function TooltipRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className="flex w-full items-center gap-2">
      <span className="size-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: color }} />
      <span className="flex-1 text-background/60">{label}</span>
      <span className="font-medium tabular-nums text-background">{value}</span>
    </div>
  )
}

/**
 * The app's near-black chart tooltip (`ChartTooltipContent`, unchanged), with
 * the card's header and rows: this period first, then the previous one.
 */
export function PeriodTooltip({
  labels,
  colors,
  format,
  ...rest
}: {
  labels: { current: string; previous: string }
  colors: { current: string; previous: string }
  format: (n: number) => string
  // Recharts injects active / payload / label
  [key: string]: unknown
}) {
  return (
    <ChartTooltipContent
      {...(rest as object)}
      className="min-w-[140px] gap-1.5 px-2.5 py-1.5"
      labelClassName="font-medium text-background/60"
      labelFormatter={(_value: unknown, payload: Array<{ payload?: PeriodPoint }>) => payload?.[0]?.payload?.tooltipLabel ?? ''}
      formatter={(value: unknown, name: unknown) => {
        const isPrev = name === 'previous'
        const n = typeof value === 'number' && Number.isFinite(value) ? format(value) : '—'
        return (
          <TooltipRow
            color={isPrev ? colors.previous : colors.current}
            label={isPrev ? labels.previous : labels.current}
            value={n}
          />
        )
      }}
    />
  )
}

/** This period first in the tooltip, whatever the draw order. */
export const currentFirst = (item: { dataKey?: unknown }) => (item.dataKey === 'current' ? 0 : 1)

/** ChartContainer sized to the card plot (the context the tooltip needs). */
export function PlotContainer({ config, children, className }: { config: ChartConfig; children: ReactNode; className?: string }) {
  return (
    <ChartContainer config={config} className={`aspect-auto h-[200px] w-full ${className ?? ''}`}>
      {children as React.ReactElement}
    </ChartContainer>
  )
}

/**
 * Loading plot: real dates, flat blocks for Y labels and seven 28px bars at a
 * fixed height, no shimmer.
 */
export function LoadingPlot({ labels }: { labels: string[] }) {
  const data = labels.map((label) => ({ label, v: 72 }))
  const ticks = [0, 164 / 3, (164 / 3) * 2, 164]
  return (
    <PlotContainer config={{}}>
      <BarChart data={data} margin={{ top: PLOT_TOP, right: 0, bottom: 0, left: 0 }} barCategoryGap="20%">
        <CartesianGrid {...gridProps} />
        {xAxis()}
        <YAxis
          width={Y_GUTTER}
          axisLine={false}
          tickLine={false}
          ticks={ticks}
          domain={[0, 164]}
          interval={0}
          tick={({ y }: { y?: number }) =>
            y == null ? <g /> : <rect x={8} y={y - 4} width={24} height={8} rx={4} fill="var(--muted)" />
          }
        />
        <Bar dataKey="v" barSize={28} radius={[4, 4, 0, 0]} fill="var(--muted)" isAnimationActive={false} />
      </BarChart>
    </PlotContainer>
  )
}
